import React from 'react';
import { FlashbarProps, StatusIndicator, TextContent } from '@amzn/awsui-components-react-v3';
import { validateContextForDataAgreement } from 'src/api/d3a';
import {
  ResourceAgreementWithD3aValidation,
  ReviewAgreementsResponse,
  ReviewAgreementsResponseList,
  ReviewDataById,
} from './types';
import { ReviewerPersona, ReviewReviewerList } from 'aws-sdk/clients/awsdlomni';
import { submitReview } from 'src/api/notifications';
import { APPROVE, DEFER, DENY, KEEP, NEEDS_MORE_DATA, REVIEW_SUBMISSION_SUCCESS_MESSAGE } from 'src/commons/constants';
import { FORM_CONTEXTS_FOR_REVIEWS } from 'src/components/datausageagreement/utils';

export const showNotification = (
  setNotifications: React.Dispatch<React.SetStateAction<FlashbarProps.MessageDefinition[]>>,
  content: string,
  type: FlashbarProps.Type,
) => {
  setNotifications([
    {
      type,
      dismissible: true,
      onDismiss: () => setNotifications([]),
      content,
    },
  ]);
};

export const createNotificationHelpers = (
  setNotifications: React.Dispatch<React.SetStateAction<FlashbarProps.MessageDefinition[]>>,
) => ({
  showError: (content: string) => showNotification(setNotifications, content, 'error'),
  showSuccess: (content: string) => showNotification(setNotifications, content, 'success'),
  showWarning: (content: string) => showNotification(setNotifications, content, 'warning'),
  showInfo: (content: string) => showNotification(setNotifications, content, 'info'),
  dismissNotifications: () => setNotifications([]),
});

export const updateAgreement = (
  setReviewersByAgreement: React.Dispatch<React.SetStateAction<ReviewAgreementsResponseList>>,
  index: number,
  updates: Partial<ResourceAgreementWithD3aValidation>,
) => {
  setReviewersByAgreement((prev) =>
    prev.map((item, i) =>
      i === index && item.agreement
        ? {
            ...item,
            agreement: {
              ...item.agreement,
              ...updates,
            },
          }
        : item,
    ),
  );
};

export const createAgreementUpdater = (
  setReviewersByAgreement: React.Dispatch<React.SetStateAction<ReviewAgreementsResponseList>>,
) => {
  return (index: number, updates: Partial<ResourceAgreementWithD3aValidation>) =>
    updateAgreement(setReviewersByAgreement, index, updates);
};

export const hasNonD3AFailures = (reviewersByAgreement: ReviewAgreementsResponseList) => {
  const nonD3aFailures: string[] = reviewersByAgreement
    .flatMap(({ agreement }) =>
      (agreement.d3AValidationOutput?.validationList ?? [])
        .filter((val) => !val.d3aValidationTypes || val.d3aValidationTypes.length === 0)
        .flatMap((val) => Object.values(val.validationResult as object))
        .map((val) => val?.reason),
    )
    .filter(Boolean);

  return nonD3aFailures.length > 0;
};

export const validateAgreements = async (
  reviewersByAgreement: ReviewAgreementsResponseList,
  updateAgreement: (index: number, updates: Partial<ResourceAgreementWithD3aValidation>) => void,
) => {
  let valid = true;

  await Promise.all(
    reviewersByAgreement.map(async ({ agreement }, idx) => {
      console.log('Validating agreement', agreement.agreementId);
      if (agreement.d3AValidationOutput?.isValid === undefined || agreement.d3AValidationOutput?.isValid === false) {
        try {
          const validateContextForDataAgreementOutput = await validateContextForDataAgreement({
            id: agreement.agreementId,
            context: agreement.agreementData ? JSON.parse(agreement.agreementData) : {},
            validationContexts: FORM_CONTEXTS_FOR_REVIEWS,
          });
          console.log('Validation result: ', validateContextForDataAgreementOutput.isValid);
          updateAgreement(idx, { d3AValidationOutput: validateContextForDataAgreementOutput });
          if (!validateContextForDataAgreementOutput.isValid && !agreement.overrideReason) {
            valid = false;
          }
        } catch (e) {
          console.log('Error when validating form inputs: ', e);
          valid = false;
        }
      }
    }),
  );

  // Check after fresh validation so we always inspect the latest output
  if (hasNonD3AFailures(reviewersByAgreement)) {
    valid = false;
  }

  return valid;
};

export const groupByReviewId = (reviewersByAgreement: ReviewAgreementsResponseList) => {
  let grouped: Record<string, ReviewDataById> = {};

  reviewersByAgreement.forEach((item) => {
    item.reviewers.forEach((reviewer) => {
      const { reviewId } = reviewer;

      if (!grouped[reviewId]) {
        grouped[reviewId] = {
          reviewId,
          reviewers: [reviewer],
          agreements: [item.agreement],
        };
      } else {
        grouped[reviewId].reviewers.push(reviewer);
        grouped[reviewId].agreements.push(item.agreement);
      }
    });
  });

  return Object.values(grouped);
};

export const renderValidationList = (validationList: any[]) => {
  if (!validationList?.length) return null;
  return (
    <TextContent>
      <h4>Validation results</h4>
      <ul>
        {validationList.flatMap((validation, vIdx) => {
          const entries = Object.entries((validation.validationResult as object) ?? {});
          if (!validation.d3aValidationTypes?.length) {
            return entries.map(([_, value]: [string, any], eIdx) => (
              <li key={`${vIdx}-${eIdx}`}>
                <b>{validation.attribute}: </b>
                {value?.reason}
              </li>
            ));
          }
          return (
            <li key={vIdx}>
              <b>{validation.attribute}: </b>
              <ul>
                {entries.map(([key, value]: [string, any]) => (
                  <li key={key}>
                    <StatusIndicator type={value?.isValid ? 'success' : 'error'}>{value?.reason}</StatusIndicator>
                  </li>
                ))}
              </ul>
            </li>
          );
        })}
      </ul>
    </TextContent>
  );
};

export const submitReviewsByAgreement = async (
  reviewersByAgreement: ReviewAgreementsResponseList,
  persona: ReviewerPersona,
  comment: string,
  action?: string,
) => {
  console.log('Submitting reviews: ', reviewersByAgreement, persona, comment, action);

  let reviewsData = groupByReviewId(reviewersByAgreement);
  for (const reviewData of reviewsData) {
    let candidateRequests = [];
    console.log('Review data: ', reviewData);
    let resourceAgreementList = null;
    if (persona === 'CONSUMER') {
      resourceAgreementList = reviewData.agreements.map((agreement) => {
        const { agreementId, agreementData } = agreement;
        return { agreementId, agreementData };
      });
    }

    let decision = reviewData.reviewers[0]?.action ?? action;

    if (decision == DEFER) {
      continue;
    }

    let updates = {
      decision: decision,
      comment: comment,
    };

    if (decision == KEEP) {
      const overrideAgreementsReason =
        reviewData.agreements
          ?.map((a) => a.overrideReason)
          .filter(Boolean)
          .join(', ') || undefined;
      updates['overrideAgreementsReason'] = overrideAgreementsReason;
      updates['resourceAgreementList'] = resourceAgreementList;

      const validationResults = reviewData.agreements
        ?.map((a) => ({
          agreementId: a.agreementId,
          isValid: a.d3AValidationOutput?.isValid,
          validationList: a.d3AValidationOutput?.validationList,
        }))
        .filter((a) => a.validationList?.length > 0);
      if (validationResults?.length > 0) {
        updates['agreementValidationResult'] = JSON.stringify(validationResults);
      }
    }

    candidateRequests.push({
      reviewId: reviewData.reviewId,
      reviewerPersona: persona,
      updates: updates,
    });
    if (reviewData.reviewers[0]?.reviewers != null) {
      for (let candidateReviewer of reviewData.reviewers[0].reviewers) {
        if (candidateReviewer.persona == persona) {
          candidateRequests.push({
            reviewId: reviewData.reviewId,
            reviewer: candidateReviewer.reviewer,
            reviewerPersona: persona,
            updates: updates,
          });
        }
      }
    }
    console.log('Submitting reviews: ', candidateRequests);
    let submitSuccess = false;
    let failureMessage = null;
    for (let request of candidateRequests) {
      try {
        let res = await submitReview(request);
        if (res?.result == REVIEW_SUBMISSION_SUCCESS_MESSAGE) {
          submitSuccess = true;
          break;
        }
      } catch (err) {
        console.log('failed to submit request:' + request);
        console.log(err);
        failureMessage = err;
      }
    }
    if (!submitSuccess) {
      throw new Error(`Failed to submit review ${reviewData.reviewId} with error: ${failureMessage}`);
    }
  }
};

export const groupReviewersByResourceAgreement = (reviewers: ReviewReviewerList) => {
  const groups = new Map<string, ReviewAgreementsResponse>();

  reviewers.forEach((reviewer) => {
    let previousAgreementAnswers = reviewer.resourceAgreementsBefore;
    if (reviewer.latestConsumerReviewRecord != null && reviewer.latestConsumerReviewRecord.resourceAgreements != null) {
      previousAgreementAnswers = reviewer.latestConsumerReviewRecord.resourceAgreements;
    }

    if (!previousAgreementAnswers?.length) {
      const keyStr = 'no-agreements';
      if (!groups.has(keyStr)) {
        groups.set(keyStr, { agreement: null, reviewers: [] });
      }
      groups.get(keyStr)!.reviewers.push(reviewer);
      return;
    }

    previousAgreementAnswers.forEach((agreement) => {
      const keyStr = `${agreement.agreementId}|${agreement.agreementData || 'no-data'}`;
      if (!groups.has(keyStr)) {
        groups.set(keyStr, { agreement: agreement, reviewers: [] });
      }
      groups.get(keyStr)!.reviewers.push(reviewer);
    });
  });

  return Array.from(groups.values());
};

export const isValidReviewId = (review: string) => {
  return review && review.match(/^rev-[a-z0-9-]{32}$/);
};

export const isCustodianAction = (action: string) => {
  return [NEEDS_MORE_DATA, APPROVE, DENY].includes(action);
};

export const convertUTCToLocalDate = (timestamp: string) => {
  const time = new Date(timestamp);
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  };
  return time.toLocaleString('en-US', options);
};

export const getAgreementDifference = (agreementData, agreementDataBefore) => {
  const differences = [];
  agreementData.forEach((currentItem) => {
    const beforeFormData = agreementDataBefore?.find((item) => item.agreementId === currentItem.agreementId);
    if (beforeFormData != null) {
      differences.push({
        agreementId: currentItem.agreementId,
        afterFormData: [currentItem],
        beforeFormData: [beforeFormData],
      });
    }
  });
  return differences;
};
